import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { compose } from 'recompose';
import { useSelector, useDispatch } from 'react-redux';

import { InfoBox } from '@react-google-maps/api';
import { withStyles } from '@material-ui/core/styles';

import { areEqual } from '../../../../utilities';
import {
  setExploreActiveDrawingObject,
  updateExploreObjectById,
  setExploreActiveEditingObject,
} from 'store/actions';

const maxLength = 50;

function ValidationMessage({ validationClassName, message }) {
  const defaultMessage = `Length must be ${maxLength} characters or less`;
  return (
    <div className={validationClassName}>
      {message ? message : defaultMessage}
    </div>
  );
}

function LabelObject({ position, classes }) {
  const dispatch = useDispatch();
  const {
    activeDrawingObject,
    activeEditingObject,
    selectedView,
    selectedObjectId,
  } = useSelector(state => state.exploreViews);

  const objectType = activeDrawingObject?.type || activeEditingObject?.type;
  const isActiveDrawingObjectCreating = activeDrawingObject?.isCreating;
  const isLabelCreating = objectType === 'Label' && isActiveDrawingObjectCreating;
  const isLabelSaving = objectType === 'Label' && activeEditingObject?.isSaving;
  const currentId = activeDrawingObject?.id || activeEditingObject?.id;

  const [focus, setFocus] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const selectedObject = useMemo(() => {
    if (!selectedView || selectedObjectId == null) return null;
    return selectedView.objects.find(object => object.id === selectedObjectId);
  }, [selectedView, selectedObjectId]);

  const textValue = useMemo(() => {
    return selectedObject?.textValue ?? '';
  }, [selectedObject]);

  const latLng = useMemo(() => {
    return selectedObject?.data ?? null;
  }, [selectedObject]);

  const visible = useMemo(() => {
    return selectedObject?.visible ?? true;
  }, [selectedObject]);

  const onRefChange = useCallback(node => {
    if (node) {
      const end = node.value.length;
      setTimeout(() => {
        node.setSelectionRange(end, end);
        node.focus();
      }, 200);
    }
  }, []);

  useEffect(() => {
    if (objectType === 'Label' && position && currentId && !focus) {
      const data = position;
      dispatch(updateExploreObjectById(currentId, { data }));
    }
  }, [position]);

  useEffect(() => {
    if (objectType === 'Label') {
      const map = document.querySelector('div[aria-label="Map"]');
      if (map) map.style.cursor = 'pointer';
    }

  }, [objectType]);

  useEffect(() => {
    if (isLabelCreating) {
      const { id } = activeDrawingObject;
      const data = position;
      dispatch(updateExploreObjectById(id, { textValue, data }));
      dispatch(setExploreActiveDrawingObject(null))
    }
  }, [isLabelCreating]);

  useEffect(() => {
    if (isLabelSaving) {
      const { id } = activeEditingObject;
      const data = position;
      dispatch(updateExploreObjectById(id, { textValue, data }));
      dispatch(setExploreActiveEditingObject(null));
    }
  }, [activeEditingObject]);

  function onChange(e) {
    if (e.target.value.length) {
      if (activeDrawingObject) {
        if (e.target.value.length > maxLength) {
          dispatch(setExploreActiveDrawingObject(
            { ...activeDrawingObject, isPlaced: false })
          );
        } else {
          dispatch(setExploreActiveDrawingObject(
            { ...activeDrawingObject, isPlaced: true })
          );
        }
      }

      if (activeEditingObject) {
        if (e.target.value.length > maxLength) {
          dispatch(setExploreActiveEditingObject(
            { ...activeEditingObject, isNotValid: true })
          );
        } else {
          dispatch(setExploreActiveEditingObject(
            { ...activeEditingObject, isNotValid: false })
          );
        }
      }
    }

    dispatch(updateExploreObjectById(currentId, { textValue: e.target.value }));
  }

  function onLoadInfoBox(infoBox) {
    infoBox.boxClass = infoBox.boxClass + ' infoBoxContainer';
  }

  function onFocus() {
    setFocus(true);
  }

  function onBlur() {
    setFocus(false);
  }

  if (objectType !== 'Label' || !latLng) return null;

  function onMouseOver() {
    setIsHovered(true);
  }

  function onMouseOut() {
    setIsHovered(false);
  }

  const isNotValid = textValue?.length > maxLength;

  return (
    <InfoBox
      position={latLng}
      visible={visible}
      onLoad={onLoadInfoBox}
      options={{
        disableAutoPan: true
      }}
    >
      <div
        className={classes.labelWrapper}
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
      >
        {isHovered && (
          <div className={classes.popupBubbleAnchor}>
            <div className={classes.popupBubble}>
              Click on map to change label position
            </div>
          </div>
        )}
        <textarea
          ref={onRefChange}
          value={textValue}
          onChange={onChange}
          className={classes.label}
          onFocus={onFocus}
          onBlur={onBlur}
          style={isNotValid ? {border: '1px solid #ff0000'} : null}
        />
        {
          isNotValid
            ? <ValidationMessage validationClassName={classes.validation} />
            : null
        }
      </div>
    </InfoBox>
  );
}

const styles = () => ({
  labelWrapper: {
    cursor: 'pointer',
    maxWidth: '13rem',
    backgroundColor: '#ffa300',
    color: '#fff',
    padding: '0.4rem',
    borderRadius: '0.125rem',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
    overflow: 'hidden',
  },
  label: {
    resize: 'none',
    overflow: 'hidden',
    border: '0px solid transparent',
    width: '12.2rem',
    '&:focus': {
      outline: 'none !important',
      border: '1px solid #ffa300',
      boxShadow: '0 0 10px #ffa300'
    }
  },
  validation: {
    color: '#ff0000'
  },
  popupBubble: {
    position: 'absolute',
    top: 0,
    left: 0,
    transform: 'translate(4%, -330%)',
    /* Style the bubble. */
    backgroundColor: 'white',
    padding: 5,
    borderRadius: 5,
    fontFamily: 'sans-serif',
    overflowY: 'auto',
    maxHeight: 60,
    boxShadow: '0px 2px 10px 1px rgba(0, 0, 0, 0.5)',
    color: '#000'
  },
  popupBubbleAnchor: {
    position: "absolute",
    width: "100%",
    bottom: 8,
    left: 0,
    "&::after": {
      top: '-3.2rem',
      left: '6.5rem',
      border: 'solid transparent',
      content: '""',
      height: 0,
      width: 0,
      position: 'absolute',
      pointerEvents: 'none',
      borderColor: 'transparent',
      borderBottomColor: 'white',
      borderLeftColor: 'white',
      borderWidth: '3px',
      marginLeft: '2px',
      boxShadow: '-6px 6px 1px -4px rgba(0,0,0,0.2), -5px 5px 2px -1px rgba(0,0,0,0.14), -4px 4px 5px 0px rgba(0,0,0,0.12)',
      transform: 'translate(-100%) rotate(-45deg) ',
      transformOrigin: '0 0'
    }
  },
});

export default compose(withStyles(styles))(React.memo(LabelObject, areEqual));
